import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "../../lib/api";
import MediaPicker from "../../components/MediaPicker";

const MEDIA_KEYS = { hero_image: "image", about_image: "image" };

const PRESETS = [
  { name: "Mor (Varsayılan)", theme_accent: "#8B5CF6", theme_accent_bright: "#A855F7", theme_accent_deep: "#5B21B6", cursor_color: "#FBBF24" },
  { name: "Altın", theme_accent: "#F59E0B", theme_accent_bright: "#FBBF24", theme_accent_deep: "#B45309", cursor_color: "#FFFFFF" },
  { name: "Zümrüt", theme_accent: "#10B981", theme_accent_bright: "#34D399", theme_accent_deep: "#047857", cursor_color: "#FBBF24" },
  { name: "Kırmızı", theme_accent: "#EF4444", theme_accent_bright: "#F87171", theme_accent_deep: "#991B1B", cursor_color: "#FBBF24" },
  { name: "Mavi", theme_accent: "#3B82F6", theme_accent_bright: "#60A5FA", theme_accent_deep: "#1E40AF", cursor_color: "#FBBF24" },
  { name: "Pembe", theme_accent: "#EC4899", theme_accent_bright: "#F472B6", theme_accent_deep: "#9D174D", cursor_color: "#FBBF24" },
  { name: "Turuncu", theme_accent: "#F97316", theme_accent_bright: "#FB923C", theme_accent_deep: "#C2410C", cursor_color: "#FBBF24" },
  { name: "Beyaz / Mono", theme_accent: "#FFFFFF", theme_accent_bright: "#F8F8F8", theme_accent_deep: "#A1A1AA", cursor_color: "#FBBF24" },
];

const GROUPS = [
  { title: "Hero", keys: [
    ["hero_kicker_tr", "Hero Kicker (TR)"], ["hero_kicker_en", "Hero Kicker (EN)"],
    ["hero_title_tr", "Hero Başlık (TR)", "textarea"], ["hero_title_en", "Hero Başlık (EN)", "textarea"],
    ["hero_highlight_tr", "Vurgu Kelime (TR)"], ["hero_highlight_en", "Vurgu Kelime (EN)"],
    ["hero_description_tr", "Hero Açıklama (TR)", "textarea"], ["hero_description_en", "Hero Açıklama (EN)", "textarea"],
    ["hero_image", "Hero Görsel", "media"],
  ]},
  { title: "İstatistikler", keys: [
    ["stats_projects", "Proje Sayısı", "number"], ["stats_brands", "Marka Sayısı", "number"],
    ["stats_views", "İzlenme"], ["stats_years", "Yıl", "number"],
  ]},
  { title: "Hakkımızda", keys: [
    ["about_title_tr", "Başlık (TR)", "textarea"], ["about_title_en", "Başlık (EN)", "textarea"],
    ["about_body_tr", "Metin (TR)", "textarea"], ["about_body_en", "Metin (EN)", "textarea"],
    ["about_image", "Görsel", "media"],
  ]},
  { title: "CTA", keys: [
    ["cta_title_tr", "Başlık (TR)", "textarea"], ["cta_title_en", "Başlık (EN)", "textarea"],
    ["cta_body_tr", "Alt Metin (TR)", "textarea"], ["cta_body_en", "Alt Metin (EN)", "textarea"],
  ]},
  { title: "İletişim & Sosyal", keys: [
    ["phone", "Telefon"], ["whatsapp", "WhatsApp (uluslararası)"], ["email", "E-posta"],
    ["website", "Web Sitesi"], ["instagram", "Instagram URL"], ["youtube", "YouTube URL"],
    ["location", "Konum"],
  ]},
  { title: "SEO & Footer", keys: [
    ["seo_title", "SEO Başlığı"], ["seo_description", "SEO Açıklama", "textarea"],
    ["footer_tr", "Footer (TR)"], ["footer_en", "Footer (EN)"],
  ]},
  { title: "🎨 Tema Renkleri", keys: [
    ["theme_accent", "Ana Vurgu Rengi", "color"],
    ["theme_accent_bright", "Parlak Vurgu", "color"],
    ["theme_accent_deep", "Koyu Vurgu", "color"],
    ["theme_bg_primary", "Arka Plan (Ana)", "color"],
    ["theme_bg_secondary", "Arka Plan (İkinci)", "color"],
    ["cursor_color", "Cursor Rengi", "color"],
  ]},
];

export default function AdminSettings() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Kaydedildi. Yayında görünecek.");
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#0A0A11] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#A855F7]";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tight text-white">Site Ayarları</h1>
          <p className="text-white/50 text-sm mt-1">Bütün metinler ve iletişim bilgileri.</p>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={busy} className="btn-primary text-[11px]"><Save size={14} /> Kaydet</button>
      </div>

      <div className="space-y-8">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-xl border border-white/5 bg-[#101018] p-6">
            <h3 className="text-white/70 uppercase tracking-widest text-xs mb-5 font-mono-ui">{g.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {g.keys.map(([k, label, type]) => (
                <label key={k} className={`block ${type === "textarea" || type === "media" ? "md:col-span-2" : ""}`}>
                  <span className="text-[10px] tracking-widest uppercase text-white/50 font-mono-ui mb-1 block">{label}</span>
                  {type === "textarea" ? (
                    <textarea data-testid={`settings-${k}`} rows={3} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} />
                  ) : type === "media" ? (
                    <MediaPicker value={form[k] || ""} accept="image" onChange={(u) => set(k, u)} />
                  ) : type === "color" ? (
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        data-testid={`settings-${k}`}
                        value={form[k] || "#000000"}
                        onChange={(e) => set(k, e.target.value)}
                        className="w-14 h-11 rounded-lg bg-[#0A0A11] border border-white/10 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form[k] || ""}
                        onChange={(e) => set(k, e.target.value)}
                        className={`flex-1 ${inputCls} font-mono-ui uppercase`}
                      />
                    </div>
                  ) : (
                    <input data-testid={`settings-${k}`} type={type || "text"} value={form[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} className={inputCls} />
                  )}
                </label>
              ))}
              {g.title.includes("Tema") && (
                <div className="md:col-span-2 pt-5 border-t border-white/5 mt-3">
                  <div className="text-[10px] tracking-widest uppercase text-white/50 font-mono-ui mb-3">
                    Hızlı Palet Seçimi
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {PRESETS.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        data-testid={`preset-${p.name}`}
                        onClick={() => setForm((f) => ({ ...f, ...p, name: undefined }))}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white text-xs transition-colors"
                      >
                        <span className="flex gap-1">
                          <span style={{ background: p.theme_accent }} className="w-3 h-3 rounded-full" />
                          <span style={{ background: p.theme_accent_bright }} className="w-3 h-3 rounded-full" />
                          <span style={{ background: p.theme_accent_deep }} className="w-3 h-3 rounded-full" />
                        </span>
                        {p.name}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-white/40 mt-3">
                    Not: Değişiklikler Kaydet'e bastıktan sonra site genelinde anında yansır.
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
